import { useQueries, useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../axios-instance";
import type {
  CallSetupInfo,
  CaseLogEntry,
  CaseLogKpi,
  EditSupportTicketDto,
  FinalReportImportResult,
  GetValidContractsResponse,
  SalesforceCaseDetails,
  SiteRootEquipmentSelection,
  SupportLineTermsAndConditions,
  SupportTicketTData,
  UploadSupportLineFileDto,
} from "../models/support-line";

export async function getSalesforceCaseApi(
  site: string,
  caseNumber: string,
  signal?: AbortSignal
) {
  return (
    await axiosInstance.get<SalesforceCaseDetails>(
      `/sites/${encodeURIComponent(site)}/support-tickets/salesforce-case`,
      { params: { caseNumber }, signal }
    )
  ).data;
}

export async function importSalesforceCaseApi(
  site: string,
  rootEquipment: string,
  data: FormData
) {
  return (
    await axiosInstance.post<string>(
      `/sites/${encodeURIComponent(site)}/root-equipment/${encodeURIComponent(rootEquipment)}/support-tickets/import-salesforce`,
      data
    )
  ).data;
}

export type CreateCaseLogDto = {
  event: string;
  dateTime: string;
  channel: string;
  from: string;
  to: string;
  notes: string;
};

import type { AttachmentInfo } from "@abb-connect/react-shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const keys = {
  all: (site?: string, rootEq?: string) =>
    [site, rootEq, "support-tickets"] as const,
  entity: (site?: string, rootEq?: string, ticketId?: string) =>
    [site, rootEq, "support-tickets", ticketId] as const,
  validContracts: (site?: string, rootEq?: string) =>
    [site, rootEq, "valid-contracts"] as const,
  callbackTestAllowed: (site?: string, rootEq?: string) =>
    [site, rootEq, "callback-test-allowed"] as const,
  termsAndConditions: (site?: string, rootEq?: string) =>
    [site, rootEq, "terms-and-conditions"] as const,
  equipment: (site?: string, rootEq?: string, productionArea?: string) =>
    [site, rootEq, "support-ticket-equipment", productionArea] as const,
  attachments: (site?: string, rootEq?: string, ticketId?: string) =>
    [site, rootEq, "support-tickets", ticketId, "attachments"] as const,
  attachmentEntity: (
    site?: string,
    rootEq?: string,
    ticketId?: string,
    attachmentId?: string
  ) =>
    [
      site,
      rootEq,
      "support-tickets",
      ticketId,
      "attachments",
      attachmentId,
    ] as const,
  caseLogs: (site?: string, rootEq?: string, ticketId?: string) =>
    [site, rootEq, "support-tickets", ticketId, "case-logs"] as const,
  caseLogKpis: (site?: string, rootEq?: string, ticketId?: string) =>
    [site, rootEq, "support-tickets", ticketId, "case-logs", "kpis"] as const,
};

export const urls = {
  all: (site: string, rootEq: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets`,
  entity: (site: string, rootEq: string, ticketId: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}`,
  validContracts: (site: string, rootEq: string) =>
    `/sites/${site}/root-equipment/${rootEq}/valid-contracts`,
  callbackTestAllowed: (site: string, rootEq: string) =>
    `/sites/${site}/root-equipment/${rootEq}/callback-test-allowed`,
  termsAndConditions: (site: string, rootEq: string) =>
    `/sites/${encodeURIComponent(site)}/root-equipment/${encodeURIComponent(rootEq)}/terms-and-conditions`,
  equipment: (site: string, rootEq: string, productionArea: string) =>
    `/sites/${encodeURIComponent(site)}/root-equipment/${encodeURIComponent(rootEq)}/support-ticket-equipment/${encodeURIComponent(productionArea)}`,
  attachments: (site: string, rootEq: string, ticketId?: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/attachments`,
  attachmentEntity: (
    site: string,
    rootEq: string,
    ticketId: string,
    attachmentId: string
  ) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/attachments/${attachmentId}`,
  caseLogs: (site: string, rootEq: string, ticketId: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/case-logs`,
  caseLogEntity: (
    site: string,
    rootEq: string,
    ticketId: string,
    caseLogId: string | number
  ) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/case-logs/${caseLogId}`,
  caseLogKpis: (site: string, rootEq: string, ticketId: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/case-logs/kpis`,
  status: (site: string, rootEq: string, ticketId: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/status`,
  escalate: (site: string, rootEq: string, ticketId: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/escalate`,
  accept: (site: string, rootEq: string, ticketId: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/accept`,
  finalReport: (site: string, rootEq: string, ticketId: string) =>
    `/sites/${site}/root-equipment/${rootEq}/support-tickets/${ticketId}/final-report`,
  importFinalReport: (site: string) =>
    `/sites/${encodeURIComponent(site)}/support-tickets/import-final-report`,
  setupInfo: (site: string) => `/sites/${site}/support-call/setup-info`,
};

export const useSupportTicketEquipmentApi = (
  site?: string,
  rootEq?: string,
  productionArea?: string,
  enabled: boolean = true
) => {
  return useQuery<string[]>({
    queryKey: keys.equipment(site, rootEq, productionArea),
    queryFn: async () => {
      const url = urls.equipment(site!, rootEq!, productionArea!);
      return (await axiosInstance.get<string[]>(url)).data;
    },
    enabled: enabled && !!site && !!rootEq && !!productionArea,
    staleTime: Infinity,
  });
};

export const useGetSupportTicketsApi = (
  site?: string,
  rootEq?: string,
  includeAttachments: boolean = false
) => {
  return useQuery<SupportTicketTData[]>({
    queryKey: keys.all(site, rootEq),
    queryFn: async () => {
      const url =
        urls.all(site!, rootEq!) + `?includeAttachments=${includeAttachments}`;
      return (await axiosInstance.get<SupportTicketTData[]>(url)).data;
    },
    enabled: !!site && !!rootEq,
    staleTime: Infinity,
  });
};

export const useGetSupportTicketsForRootEquipmentsApi = (
  selection: SiteRootEquipmentSelection = [],
  includeAttachments: boolean = false
) => {
  const pairs = selection.flatMap(({ site, rootEquipments }) =>
    rootEquipments.map((rootEquipment) => ({ site, rootEquipment }))
  );

  return useQueries({
    queries: pairs.map(({ site, rootEquipment }) => ({
      queryKey: keys.all(site, rootEquipment),
      queryFn: async () => {
        const url =
          urls.all(site, rootEquipment) +
          `?includeAttachments=${includeAttachments}`;
        const data = (await axiosInstance.get<SupportTicketTData[]>(url)).data;
        return data.map((ticket) => ({ ...ticket, site }));
      },
      enabled: pairs.length > 0,
      staleTime: Infinity,
    })),
  });
};

export const useGetSupportTicketAttachmentsApi = (
  site?: string,
  rootEq?: string,
  ticketId?: string
) => {
  return useQuery<AttachmentInfo[]>({
    queryKey: keys.attachments(site, rootEq, ticketId),
    queryFn: async () => {
      if (!site || !rootEq) return [];
      const url = urls.attachments(site, rootEq, ticketId);
      return (await axiosInstance.get<AttachmentInfo[]>(url)).data;
    },
    staleTime: Infinity,
  });
};

export const useGetCaseLogsApi = (
  site?: string,
  rootEq?: string,
  ticketId?: string,
  enabled: boolean = true
) => {
  return useQuery<CaseLogEntry[]>({
    queryKey: keys.caseLogs(site, rootEq, ticketId),
    queryFn: async () => {
      if (!site || !rootEq || !ticketId) return [];
      const url = urls.caseLogs(site, rootEq, ticketId);
      return (await axiosInstance.get<CaseLogEntry[]>(url)).data;
    },
    staleTime: Infinity,
    enabled,
  });
};

export const useCreateCaseLogApi = (site?: string, rootEquipment?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketNumber,
      caseLog,
    }: {
      ticketNumber: string;
      caseLog: CreateCaseLogDto;
    }) => {
      if (!site || !rootEquipment)
        throw new Error("Site and root equipment are required");
      const url = urls.caseLogs(site, rootEquipment, ticketNumber);
      return axiosInstance.post<CaseLogEntry>(url, caseLog);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.caseLogs(site, rootEquipment, variables.ticketNumber),
      });
    },
  });
};

export const useUpdateCaseLogApi = (site?: string, rootEquipment?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketNumber,
      caseLogId,
      caseLog,
    }: {
      ticketNumber: string;
      caseLogId: number;
      caseLog: CreateCaseLogDto;
    }) => {
      if (!site || !rootEquipment)
        throw new Error("Site and root equipment are required");
      const url = urls.caseLogEntity(
        site,
        rootEquipment,
        ticketNumber,
        caseLogId
      );
      return axiosInstance.put<CaseLogEntry>(url, caseLog);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.caseLogs(site, rootEquipment, variables.ticketNumber),
      });
    },
  });
};

export const useDeleteCaseLogApi = (site?: string, rootEquipment?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ticketNumber,
      caseLogId,
    }: {
      ticketNumber: string;
      caseLogId: number;
    }) => {
      if (!site || !rootEquipment)
        throw new Error("Site and root equipment are required");
      const url = urls.caseLogEntity(
        site,
        rootEquipment,
        ticketNumber,
        caseLogId
      );
      return axiosInstance.delete<void>(url);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.caseLogs(site, rootEquipment, variables.ticketNumber),
      });
    },
  });
};

export const useGetCaseLogKpisApi = (
  site?: string,
  rootEq?: string,
  ticketId?: string,
  enabled: boolean = true
) => {
  return useQuery<CaseLogKpi>({
    queryKey: keys.caseLogKpis(site, rootEq, ticketId),
    queryFn: async () => {
      if (!site || !rootEq || !ticketId)
        return {
          firstResponseMinutes: null,
          solutionDeliveryMinutes: null,
          caseResolutionMinutes: null,
        };
      const url = urls.caseLogKpis(site, rootEq, ticketId);
      return (await axiosInstance.get<CaseLogKpi>(url)).data;
    },
    staleTime: Infinity,
    enabled,
  });
};

/**
 * POST a new support ticket (multipart/form-data).
 * Uses relative URL; lets axios set the multipart boundary.
 */
export async function createSupportTicketApi(
  customer: string,
  rootEquipment: string,
  formData: FormData,
  options?: {
    createAsAccepted?: boolean;
    createDefaultCaseLogEntry?: boolean;
  }
): Promise<string> {
  if (options?.createAsAccepted !== undefined) {
    formData.append("CreateAsAccepted", String(options.createAsAccepted));
  }
  if (options?.createDefaultCaseLogEntry !== undefined) {
    formData.append(
      "CreateDefaultCaseLogEntry",
      String(options.createDefaultCaseLogEntry)
    );
  }
  const url = urls.all(customer, rootEquipment);
  const res = await axiosInstance.post<string>(url, formData);
  return res.data;
}

export const useEditSupportTicketApi = (site?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rootEquipment,
      ticketNumber,
      dto,
    }: {
      rootEquipment: string;
      ticketNumber: string;
      dto: EditSupportTicketDto;
    }) => {
      if (!site) throw new Error("Site is required");
      const url = urls.entity(site, rootEquipment, ticketNumber);
      return axiosInstance.put<{ serviceHistoryEventCreated: boolean }>(
        url,
        dto
      );
    },
    onSuccess: async (response, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.all(site, variables.rootEquipment),
      });

      if (response.data.serviceHistoryEventCreated) {
        await queryClient.invalidateQueries({
          //FIXME: Use the key function from service-events-api.ts
          queryKey: [site, "service-events", variables.rootEquipment],
        });
      }
    },
  });
};

export const useAcceptSupportTicketApi = (site?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rootEquipment,
      ticketNumber,
    }: {
      rootEquipment: string;
      ticketNumber: string;
    }) => {
      if (!site) throw new Error("Site is required");
      const url = urls.accept(site, rootEquipment, ticketNumber);
      return axiosInstance.post(url);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.all(site, variables.rootEquipment),
      });
      await queryClient.invalidateQueries({
        queryKey: keys.caseLogs(
          site,
          variables.rootEquipment,
          variables.ticketNumber
        ),
      });
    },
  });
};

export const useEscalateSupportTicketApi = (site?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rootEquipment,
      ticketNumber,
    }: {
      rootEquipment: string;
      ticketNumber: string;
    }) => {
      if (!site) throw new Error("Site is required");
      const url = urls.escalate(site, rootEquipment, ticketNumber);
      return axiosInstance.post(url);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.all(site, variables.rootEquipment),
      });
      await queryClient.invalidateQueries({
        queryKey: keys.caseLogs(
          site,
          variables.rootEquipment,
          variables.ticketNumber
        ),
      });
    },
  });
};

export const useUpdateSupportTicketStatusApi = (site?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rootEquipment,
      ticketNumber,
      status,
    }: {
      rootEquipment: string;
      ticketNumber: string;
      status: number;
    }) => {
      if (!site) throw new Error("Site is required");
      const url = urls.status(site, rootEquipment, ticketNumber);
      return axiosInstance.patch(url, status, {
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.all(site, variables.rootEquipment),
      });
    },
  });
};

export function useUploadSupportTicketAttachmentApi(
  site?: string,
  rootEquipment?: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketNumber, file }: UploadSupportLineFileDto) => {
      if (!site || !rootEquipment) return;
      const url = urls.attachments(site, rootEquipment, ticketNumber);
      const formData = new FormData();
      formData.append("attachments", file);

      await axiosInstance.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: async () => {
      if (!site) return;
      await queryClient.invalidateQueries({
        queryKey: keys.all(site, rootEquipment),
      });
    },
  });
}

export function useUploadFinalReportApi(site?: string, rootEquipment?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketNumber, file }: UploadSupportLineFileDto) => {
      if (!site || !rootEquipment) return undefined;
      const url = urls.finalReport(site, rootEquipment, ticketNumber);
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post<
        { serviceHistoryEventCreated?: boolean } | ""
      >(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data || undefined;
    },
    onSuccess: async (data) => {
      if (!site) return;
      await queryClient.invalidateQueries({
        queryKey: keys.all(site, rootEquipment),
      });

      if (data?.serviceHistoryEventCreated) {
        await queryClient.invalidateQueries({
          //FIXME: Use the key function from service-events-api.ts
          queryKey: [site, "service-events", rootEquipment],
        });
      }
    },
  });
}

/**
 * Extracts case fields from a Final Report PDF via the platform Copilot model.
 * `productionArea` lets the backend match the affected equipment to the catalog.
 */
export async function importFinalReportApi(
  site: string,
  file: File,
  productionArea?: string
): Promise<FinalReportImportResult> {
  const formData = new FormData();
  formData.append("file", file);
  if (productionArea) {
    formData.append("productionArea", productionArea);
  }

  const url = urls.importFinalReport(site);
  const res = await axiosInstance.post<FinalReportImportResult>(url, formData);
  return res.data;
}

export function useUploadSupportTicketAttachmentsApi(
  site?: string,
  rootEquipment?: string
) {
  const { mutateAsync } = useUploadSupportTicketAttachmentApi(
    site,
    rootEquipment
  );

  const uploadSupportTicketAttachments = async (
    dtos: UploadSupportLineFileDto[]
  ) => {
    const mutationPromises = dtos.map((dto) => mutateAsync(dto));
    return await Promise.all(mutationPromises);
  };

  return { uploadSupportTicketAttachments };
}

type DocDeleteVars = {
  ticketNumber: string;
  attachmentId: string;
};

export function useDeleteSupportTicketAttachmentApi(
  site?: string,
  rootEquipment?: string
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DocDeleteVars>({
    mutationFn: async ({ ticketNumber, attachmentId }) => {
      if (!site || !rootEquipment) return;
      const url = urls.attachmentEntity(
        site,
        rootEquipment,
        ticketNumber,
        attachmentId
      );
      await axiosInstance.delete<void>(url);
    },
    onSuccess: async () => {
      if (site) {
        await queryClient.invalidateQueries({
          queryKey: keys.all(site, rootEquipment),
        });
      }
    },
  });
}

export function useDeleteSupportTicketAttachmentsApi(
  site?: string,
  rootEquipment?: string
) {
  const { mutateAsync } = useDeleteSupportTicketAttachmentApi(
    site,
    rootEquipment
  );

  const deleteSupportTicketAttachments = async ({
    ticketNumber,
    attachmentIds,
  }: {
    ticketNumber: string;
    attachmentIds: string[];
  }) => {
    const mutationPromises = attachmentIds.map((attachmentId) =>
      mutateAsync({ ticketNumber, attachmentId })
    );
    return await Promise.all(mutationPromises);
  };

  return { deleteSupportTicketAttachments };
}

export const useDeleteSupportTicketApi = (site?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rootEquipment,
      ticketNumber,
    }: {
      rootEquipment: string;
      ticketNumber: string;
    }) => {
      if (!site) throw new Error("Site is required");
      const url = urls.entity(site, rootEquipment, ticketNumber);
      return axiosInstance.delete(url);
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: keys.all(site, variables.rootEquipment),
      });
    },
  });
};

/**
 * GET valid contracts for a customer (site) and root equipment.
 * Uses relative URLs like equipment-api.ts.
 */
export function useValidContractsApi(
  customer?: string,
  rootEquipment?: string
) {
  return useQuery<GetValidContractsResponse>({
    queryKey: keys.validContracts(customer, rootEquipment),
    queryFn: async () => {
      if (!customer || !rootEquipment) return [];
      const url = urls.validContracts(customer, rootEquipment);
      return (await axiosInstance.get<GetValidContractsResponse>(url)).data;
    },
    staleTime: Infinity,
  });
}

/**
 * GET whether the current user is allowed to create a SupportLine callback
 * test case for a given site (customer) and root equipment (mill).
 */
export function useCallbackTestAllowedApi(
  customer?: string,
  rootEquipment?: string
) {
  return useQuery<boolean>({
    queryKey: keys.callbackTestAllowed(customer, rootEquipment),
    queryFn: async () => {
      if (!customer || !rootEquipment) return false;
      const url = urls.callbackTestAllowed(customer, rootEquipment);
      return (await axiosInstance.get<boolean>(url)).data;
    },
    enabled: !!customer && !!rootEquipment,
    staleTime: 30_000,
  });
}

/** GET the active SupportLine 24/7 Terms and Conditions; null when the feature is switched off. */
export function useSupportLineTermsApi(
  customer?: string,
  rootEquipment?: string,
  enabled = true
) {
  return useQuery<SupportLineTermsAndConditions | null>({
    queryKey: keys.termsAndConditions(customer, rootEquipment),
    queryFn: async () => {
      const url = urls.termsAndConditions(customer!, rootEquipment!);
      const response =
        await axiosInstance.get<SupportLineTermsAndConditions>(url);
      return response.status === 204 ? null : response.data;
    },
    enabled: enabled && !!customer && !!rootEquipment,
    staleTime: 5 * 60_000,
  });
}

export const getCallSetupInfo = async (
  customer: string,
  signal?: AbortSignal
): Promise<CallSetupInfo> => {
  const url = urls.setupInfo(customer);
  const { data } = await axiosInstance.get<CallSetupInfo>(url, { signal });
  return data;
};
